import React, { useState, useEffect } from "react";
import {
  Brain,
  Calendar,
  Mail,
  Clock,
  CheckCircle,
  Menu,
  X,
  ArrowRight,
  User,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Video,
  MapPin,
  Phone,
  Heart,
  AlertCircle,
  Loader2,
} from "lucide-react";

/* =========================================================================
   KONFIGURÁCIÓ (ITT MÓDOSÍTSD AZ ADATOKAT)
   ========================================================================= */

// 1. KÉPEK ELÉRÉSI ÚTVONALA
// Ide írd be a feltöltött képek pontos nevét (vagy URL-jét)
const LOGO_IMAGE_PATH = "logo.png";
const PROFILE_IMAGE_PATH = "profil.jpg";
const ATMOSPHERE_IMAGE_PATH = "atmoszfera.jpg";
// 2. GOOGLE CALENDAR BEÁLLÍTÁSOK
// Ezt a lentebb leírt útmutató alapján tudod kitölteni
const GOOGLE_CALENDAR_CONFIG = {
  apiKey: "AIzaSyCPSxn7lyXn_CYF9xb_PZ5YRy3SR4-hgIU",
  calendarId:
    "784c8d56c38eb1a8237d45bca5ffa5239d173bfc404ce763aadf9906d66edd7e@group.calendar.google.com", // Vagy a naptár specifikus ID-ja (pl. email cím)
};

// 3. EMAILJS BEÁLLÍTÁSOK (Értesítés foglaláskor)
// Regisztrálj az https://www.emailjs.com/ oldalon, és írd be az adataidat!
const EMAILJS_CONFIG = {
  publicKey: "NbEo9p6EQKQX67u-c", // Pl.: "user_xxxxxxxxxxxx"
  serviceId: "service_9z1hbgk", // Pl.: "service_gmail"
  templateId: "template_qsumsfn", // Pl.: "template_booking"
};

/* =========================================================================
   SEGÉDFÜGGVÉNYEK & KOMPONENSEK
   ========================================================================= */

const formatDate = (date) => {
  return new Intl.DateTimeFormat("hu-HU", {
    month: "short",
    day: "numeric",
  }).format(date);
};

const getDayName = (date) => {
  return new Intl.DateTimeFormat("hu-HU", { weekday: "long" }).format(date);
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneStructureRegex = /^[\+]?[\d\s\-]+$/;

// Loading Spinner Komponens
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center py-10 space-y-4 text-[#A6998A]">
    <Loader2 className="h-8 w-8 animate-spin" />
    <span className="text-sm font-medium">Időpontok keresése...</span>
  </div>
);

const Navigation = ({
  currentView,
  scrollToSection,
  navigateToBooking,
  isMenuOpen,
  setIsMenuOpen,
}) => (
  <nav className="bg-[#FDFBF7] shadow-sm sticky top-0 z-50 font-sans border-b border-[#E6E2DD]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between h-20">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => scrollToSection("hero")}
        >
          <div className="flex items-center gap-3">
            <img
              src={LOGO_IMAGE_PATH}
              alt="NeuroNest Logo"
              className="h-12 w-auto object-contain mix-blend-multiply opacity-90"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <span className="text-2xl font-serif text-[#5D5750] tracking-wide font-medium hidden sm:block">
              NeuroNest
            </span>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-8">
          <button
            onClick={() => scrollToSection("hero")}
            className="text-[#8C857B] hover:text-[#5D5750] font-medium transition-colors"
          >
            Kezdőlap
          </button>
          <button
            onClick={() => scrollToSection("about")}
            className="text-[#8C857B] hover:text-[#5D5750] font-medium transition-colors"
          >
            Rólam
          </button>
          <button
            onClick={() => scrollToSection("services")}
            className="text-[#8C857B] hover:text-[#5D5750] font-medium transition-colors"
          >
            Miben segíthetek?
          </button>
          <button
            onClick={() => scrollToSection("prices")}
            className="text-[#8C857B] hover:text-[#5D5750] font-medium transition-colors"
          >
            Árak
          </button>
          <button
            onClick={navigateToBooking}
            className={`px-6 py-2 rounded-full font-medium transition-all shadow-sm tracking-wide ${
              currentView === "booking"
                ? "bg-[#8C7B6B] text-white"
                : "bg-[#A6998A] text-white hover:bg-[#8C7B6B]"
            }`}
          >
            Időpontfoglalás
          </button>
        </div>
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-[#8C857B] p-2"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>
    </div>
    {isMenuOpen && (
      <div className="md:hidden bg-[#FDFBF7] border-t border-[#E6E2DD] absolute w-full shadow-lg z-50">
        <div className="px-4 pt-4 pb-6 space-y-2">
          {/* Mobil menü gombok */}
          {["hero", "about", "services", "prices"].map((section) => (
            <button
              key={section}
              onClick={() => scrollToSection(section)}
              className="block w-full text-left px-3 py-3 text-[#5D5750] hover:bg-[#F5F0EB] rounded-lg text-lg capitalize"
            >
              {section === "hero"
                ? "Kezdőlap"
                : section === "about"
                ? "Rólam"
                : section === "services"
                ? "Szolgáltatások"
                : "Árak"}
            </button>
          ))}
          <button
            onClick={navigateToBooking}
            className="block w-full text-left px-3 py-3 text-[#8C7B6B] font-bold bg-[#F0EBE5] rounded-lg mt-4 text-lg"
          >
            Időpontfoglalás
          </button>
        </div>
      </div>
    )}
  </nav>
);

const HomeView = ({ navigateToBooking, scrollToSection }) => (
  <>
    <div id="hero" className="relative bg-[#FDFBF7] overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center min-h-[auto] lg:min-h-[85vh]">
          <div className="w-full lg:w-1/2 pt-12 pb-12 px-4 sm:px-6 lg:px-8 lg:py-20 z-10">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl tracking-tight font-extrabold text-[#4A453E] sm:text-5xl md:text-6xl font-serif">
                <div className="flex items-center justify-center lg:justify-start gap-4 mb-2">
                  <span className="block xl:inline">NeuroNest</span>
                </div>
                <span className="block text-[#A6998A] text-2xl sm:text-3xl mt-2 font-sans font-medium leading-relaxed">
                  Pszichológiai támogatás neurodivergens gyermeket nevelő
                  szülőknek
                </span>
              </h1>
              <p className="mt-6 text-base text-[#756F66] sm:mt-8 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-8 md:text-xl lg:mx-0 leading-relaxed">
                A nevem Lévay Dóra, pszichológus vagyok. A NeuroNest-et azért
                hoztam létre, hogy legyen egy biztonságos, elfogadó „fészek”,
                ahol egyéni folyamat keretében támogatást, megértést és
                edukációt kaphatnak azok a szülők, akiknek a gyermeke az
                átlagostól eltérő fejlődésmenetet mutat.
              </p>
              <div className="mt-8 sm:mt-10 sm:flex sm:justify-center lg:justify-start gap-4">
                <button
                  onClick={navigateToBooking}
                  className="w-full sm:w-auto px-8 py-3 rounded-md text-white bg-[#A6998A] hover:bg-[#8C7B6B] font-medium shadow-md transition-all"
                >
                  Időpontot foglalok
                </button>
                <button
                  onClick={() => scrollToSection("services")}
                  className="mt-3 sm:mt-0 w-full sm:w-auto px-8 py-3 border border-[#A6998A] rounded-md text-[#8C7B6B] hover:bg-[#F5F0EB] font-medium transition-all"
                >
                  Miben segíthetek?
                </button>
              </div>
            </div>
          </div>
          <div className="w-full lg:w-1/2 lg:absolute lg:inset-y-0 lg:right-0 lg:h-full bg-[#F0EBE5] flex items-center justify-center overflow-hidden h-48 lg:h-auto order-first lg:order-last">
            {/* MÓDOSÍTÁS: object-center hozzáadva a központosításhoz */}
            <img
              src={ATMOSPHERE_IMAGE_PATH}
              alt="Atmosphere"
              className="relative w-full h-full object-cover object-center opacity-90 mix-blend-multiply brightness-95 grayscale-[0.2]"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        </div>
      </div>
    </div>

    <div id="services" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-base text-[#A6998A] font-semibold tracking-wide uppercase">
            Szolgáltatások
          </h2>
          <p className="mt-2 text-3xl font-extrabold text-[#4A453E] sm:text-4xl font-serif">
            Miben segíthetek?
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              icon: Brain,
              title: "Pszichoedukáció",
              desc: "Ha szeretné jobban megérteni gyermeke működését, illetve a neurodivergens állapotok sajátosságait.",
            },
            {
              icon: CheckCircle,
              title: "Diagnózis feldolgozása",
              desc: "Ha gyermeke diagnózisát nehezen tudja feldolgozni, elfogadni.",
            },
            {
              icon: User,
              title: "Élethelyzeti nehézségek",
              desc: "Ha segítséget szeretne kapni fokozott érzelmi terheléssel járó élethelyzetek kezeléséhez (pl. intézménykeresés, testvér érkezése, stb).",
            },
            {
              icon: Heart,
              title: "Érzelmi fásultság, kiégés",
              desc: "Ha a mindennapi nehézségekben kimerült és úgy érzi, hogy nem talál értő fülekre.",
            },
          ].map((service, idx) => (
            <div
              key={idx}
              className="bg-[#FDFBF7] p-8 border-l-4 border-[#C8BFA9] rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center mb-4">
                <div className="bg-[#F0EBE5] p-3 rounded-full">
                  <service.icon className="h-6 w-6 text-[#8C7B6B]" />
                </div>
                <h3 className="ml-4 text-xl font-bold text-[#5D5750]">
                  {service.title}
                </h3>
              </div>
              <p className="text-[#756F66]">{service.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div id="about" className="py-20 bg-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
          <div className="relative mb-10 lg:mb-0">
            <img
              className="rounded-2xl shadow-xl w-full object-cover"
              src={PROFILE_IMAGE_PATH}
              alt="Lévay Dóra"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <div className="mt-4">
            <div className="mb-2">
              <h2 className="text-base text-[#A6998A] font-semibold tracking-wide uppercase mb-2">
                Bemutatkozás
              </h2>
            </div>
            <h2 className="text-3xl font-extrabold text-[#4A453E] font-serif mb-2">
              Lévay Dóra
            </h2>
            <p className="text-lg text-[#8C7B6B] font-medium mb-6">
              Pszichológus, Coach, Neuroaffirmatív szakember
            </p>
            <div className="prose prose-stone text-[#5D5750] space-y-4 text-lg leading-relaxed text-justify">
              <p>
                Tanulmányaimat az ELTE PPK-n végeztem, ahol 2009-ben diplomáztam
                pszichológusként (Msc). Huszonévesen, az egyetemről kikerülve a
                munkapszichológia világa vonzott leginkább, így az elmúlt 15
                évet nemzetközi vállalatoknál töltöttem HR szakemberként.
                2020-ban akkreditált Coach képesítést szereztem a Legacy
                Kft-nél, azóta egyéni coaching folyamatokat is végzek. Jelenleg
                elsősorban online konzultációk formájában dolgozom.
              </p>
              <p>
                Az utóbbi években szevedélyemmé vált a neurodiverzitás témája,
                miután én magam is neurodivergens gyermek édesanyja vagyok,
                ezáltal saját bőrömön tapasztaltam meg, hogy milyen sokrétű
                kihívások elé állítja a szülőket, ha gyermekük atipikusan
                fejlődik.
              </p>
              <p>
                Így érett meg a döntés, hogy gyökereimhez visszatérve,
                pszichológusként nyújtsak támogatást a hasonló helyzetben lévő
                szülőknek, mert erősen hiszem, hogy akkor tudjuk jól támogatni
                gyermekeinket, ha mi magunk is jól vagyunk. Ugyanakkor pontosan
                a neurodivergens gyermekek magasabb támogatási szükségletei
                miatt nagyon nehéz a szülőknek magukra is időt, figyelmet
                fordítani, vagy akár csak megértést találni a közvetlen
                környezetükben.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="prices" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-base text-[#A6998A] font-semibold tracking-wide uppercase">
            Információk
          </h2>
          <p className="mt-2 text-3xl font-extrabold text-[#4A453E] sm:text-4xl font-serif">
            Árak és Kapcsolat
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Ár kártyák */}
          {[
            {
              type: "Online Konzultáció",
              price: "14.000 Ft",
              desc: "50 perc",
              icon: Video,
            },
            {
              type: "Személyes Konzultáció",
              price: "16.000 Ft",
              desc: "50 perc • Önismereti Zug - 1142 Budapest, Erzsébet királyné útja 67.",
              icon: User,
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E6E2DD] hover:border-[#C8BFA9] transition-all shadow-sm flex flex-col items-center text-center"
            >
              <div className="bg-[#F0EBE5] p-4 rounded-full mb-4">
                <item.icon className="h-8 w-8 text-[#8C7B6B]" />
              </div>
              <h3 className="text-xl font-bold text-[#5D5750] mb-2">
                {item.type}
              </h3>
              <p className="text-[#8C857B] font-medium mb-6">Egyéni alkalom</p>
              <div className="text-3xl font-serif font-bold text-[#4A453E] mb-2">
                {item.price}
              </div>
              <p className="text-sm text-[#756F66] mb-6">{item.desc}</p>
              <button
                onClick={navigateToBooking}
                className="mt-auto px-8 py-2 rounded-full border border-[#8C7B6B] text-[#8C7B6B] hover:bg-[#8C7B6B] hover:text-white transition-colors"
              >
                Időpontot foglalok
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

const BookingView = ({
  scrollToSection,
  weekOffset,
  setWeekOffset,
  weekRangeLabel,
  calendarEvents,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  clientForm,
  handleInputChange,
  handleBooking,
  isFormValid,
  isGdprAccepted,
  setIsGdprAccepted,
  formErrors,
  isBookingSuccess,
  resetBooking,
  isLoading,
  apiError,
}) => {
  return (
    <div className="py-12 bg-[#FDFBF7] min-h-screen animate-in fade-in duration-500">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => {
            if (isBookingSuccess) resetBooking();
            scrollToSection("hero");
          }}
          className="mb-8 flex items-center text-[#8C857B] hover:text-[#5D5750] transition-colors font-medium"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Vissza a főoldalra
        </button>
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-[#4A453E] font-serif">
            Időpontfoglalás
          </h2>
          <p className="mt-4 text-lg text-[#756F66]">
            Válasszon egy megfelelő időpontot az online konzultációhoz.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#E6E2DD]">
          <div className="md:flex min-h-[500px]">
            {/* BAL OLDAL: Dátum választó */}
            <div
              className={`md:w-5/12 p-6 md:p-8 border-b md:border-r border-[#F0EBE5] bg-white flex flex-col ${
                isBookingSuccess ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <h3 className="text-lg font-bold text-[#5D5750] mb-6 flex items-center">
                <Calendar className="h-5 w-5 mr-3 text-[#A6998A]" />
                1. Dátum kiválasztása
              </h3>
              <div className="flex items-center justify-between bg-[#FDFBF7] p-3 rounded-lg mb-4 border border-[#F0EBE5]">
                <button
                  onClick={() => setWeekOffset((prev) => prev - 1)}
                  disabled={weekOffset === 0}
                  className={`p-2 rounded-full ${
                    weekOffset === 0
                      ? "text-gray-300"
                      : "text-[#8C857B] hover:bg-[#E6E2DD]"
                  }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="font-medium text-[#5D5750] text-sm">
                  {weekRangeLabel}
                </span>
                <button
                  onClick={() => setWeekOffset((prev) => prev + 1)}
                  className="p-2 rounded-full text-[#8C857B] hover:bg-[#E6E2DD]"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-3 flex-grow overflow-y-auto max-h-[400px]">
                {isLoading && <LoadingSpinner />}

                {apiError && !isLoading && (
                  <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm text-center">
                    <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                    {apiError}
                  </div>
                )}

                {!isLoading && !apiError && calendarEvents.length === 0 && (
                  <p className="text-center text-gray-400 py-4 italic">
                    Nincs szabad időpont ezen a héten.
                    <br />
                    Próbálja a következő hetet!
                  </p>
                )}

                {!isLoading &&
                  calendarEvents.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedDate(item);
                        setSelectedTime(null);
                      }}
                      className={`w-full flex justify-between items-center p-4 rounded-xl border transition-all ${
                        selectedDate?.date === item.date
                          ? "border-[#A6998A] bg-[#F5F0EB] ring-1 ring-[#A6998A]"
                          : "border-[#F0EBE5] hover:bg-[#FDFBF7]"
                      }`}
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-bold text-[#5D5750] text-lg">
                          {item.day}
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[#8C857B] text-sm">
                            {item.date}
                          </span>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 uppercase font-semibold ${
                              item.type === "online"
                                ? "bg-blue-50 text-blue-600"
                                : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {item.type === "online" ? (
                              <Video size={10} />
                            ) : (
                              <MapPin size={10} />
                            )}
                            {item.type === "online" ? "Online" : "Személyes"}
                          </span>
                        </div>
                      </div>
                      {selectedDate?.date === item.date && (
                        <CheckCircle className="h-5 w-5 text-[#A6998A]" />
                      )}
                    </button>
                  ))}
              </div>
            </div>

            {/* JOBB OLDAL: Időpont és Űrlap */}
            <div className="md:w-7/12 p-6 md:p-8 bg-[#FDFBF7] flex flex-col relative min-h-[500px]">
              {isBookingSuccess ? (
                <div className="h-full flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm p-8 text-center animate-in fade-in duration-500">
                  <div className="bg-green-100 p-4 rounded-full mb-6">
                    <CheckCircle className="h-16 w-16 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#5D5750] mb-2 font-serif">
                    Sikeres foglalás!
                  </h3>

                  <div className="bg-[#FDFBF7] p-6 rounded-xl border border-[#E6E2DD] mb-6 w-full max-w-sm shadow-sm">
                    <p className="text-[#5D5750] font-medium mb-1">
                      Kiválasztott időpont:
                    </p>
                    <p className="text-[#8C7B6B] font-bold text-lg mb-3">
                      {selectedDate?.date}{" "}
                      <span className="text-sm font-normal">
                        ({selectedDate?.day})
                      </span>{" "}
                      • {selectedTime}
                    </p>

                    <div className="flex justify-center">
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                          selectedDate?.type === "online"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {selectedDate?.type === "online" ? (
                          <Video size={14} />
                        ) : (
                          <MapPin size={14} />
                        )}
                        {selectedDate?.type === "online"
                          ? "Online konzultáció"
                          : "Személyes konzultáció"}
                      </span>
                    </div>
                  </div>

                  <p className="text-[#756F66] mb-8 max-w-md leading-relaxed">
                    Kedves {clientForm.name}! <br />
                    Az igényét rögzítettük. A további információkkal hamarosan
                    keresni fogom a megadott e-mail címen.
                  </p>

                  <button
                    onClick={() => {
                      resetBooking();
                      scrollToSection("hero");
                    }}
                    className="bg-[#A6998A] text-white px-8 py-3 rounded-full font-bold hover:bg-[#8C7B6B] transition-colors shadow-md"
                  >
                    Vissza a kezdőlapra
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-[#5D5750] mb-4 flex items-center">
                      <Clock className="h-5 w-5 mr-3 text-[#A6998A]" />
                      2. Szabad időpontok
                    </h3>
                    {!selectedDate ? (
                      <div className="bg-white p-6 rounded-xl border border-dashed border-[#C8BFA9] text-center text-[#C8BFA9]">
                        <p>Kérem válasszon napot</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {selectedDate.slots.map((time, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedTime(time)}
                            className={`py-2 px-2 rounded-lg text-sm font-medium transition-all ${
                              selectedTime === time
                                ? "bg-[#A6998A] text-white"
                                : "bg-white text-[#5D5750] border border-[#E6E2DD] hover:border-[#C8BFA9]"
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedDate && selectedTime && (
                    <div className="border-t border-[#E6E2DD] pt-6 mt-auto">
                      <h3 className="text-lg font-bold text-[#5D5750] mb-4 flex items-center">
                        <User className="h-5 w-5 mr-3 text-[#A6998A]" />
                        3. Kapcsolattartási adatok
                      </h3>
                      <div className="space-y-4 mb-6">
                        <input
                          type="text"
                          name="name"
                          value={clientForm.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-[#E6E2DD] outline-none focus:border-[#A6998A]"
                          placeholder="Teljes név"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <input
                              type="tel"
                              name="phone"
                              value={clientForm.phone}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-2 rounded-lg border outline-none ${
                                formErrors.phone
                                  ? "border-red-400 bg-red-50"
                                  : "border-[#E6E2DD] focus:border-[#A6998A]"
                              }`}
                              placeholder="Telefonszám"
                            />
                            {formErrors.phone && (
                              <p className="text-xs text-red-500 mt-1">
                                {formErrors.phone}
                              </p>
                            )}
                          </div>
                          <div>
                            <input
                              type="email"
                              name="email"
                              value={clientForm.email}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-2 rounded-lg border outline-none ${
                                formErrors.email
                                  ? "border-red-400 bg-red-50"
                                  : "border-[#E6E2DD] focus:border-[#A6998A]"
                              }`}
                              placeholder="Email cím"
                            />
                            {formErrors.email && (
                              <p className="text-xs text-red-500 mt-1">
                                {formErrors.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <label className="flex items-start gap-3 text-sm text-[#5D5750] mb-6">
                        <input
                          type="checkbox"
                          checked={isGdprAccepted}
                          onChange={(e) => setIsGdprAccepted(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-[#E6E2DD] text-[#A6998A] focus:ring-[#A6998A]"
                        />
                        <span>
                          Az <a
                            href="/Adatvedelmi_tajekoztato_neuronest.pdf"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#8C7B6B] underline hover:text-[#5D5750]"
                          >
                            adatvédelmi tájékoztató
                          </a>{" "}
                          tartalmát megismertem és elfogadom
                        </span>
                      </label>
                      <button
                        onClick={handleBooking}
                        disabled={!isFormValid}
                        className={`w-full py-4 rounded-xl font-bold text-lg flex justify-center items-center shadow-md transition-all ${
                          isFormValid
                            ? "bg-[#A6998A] text-white hover:bg-[#8C7B6B]"
                            : "bg-[#E6E2DD] text-[#8C857B] cursor-not-allowed"
                        }`}
                      >
                        Foglalás véglegesítése{" "}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- FŐ KOMPONENS --- */

export default function App() {
  const [currentView, setCurrentView] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Foglalási állapotok
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isBookingSuccess, setIsBookingSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isGdprAccepted, setIsGdprAccepted] = useState(false);

  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [formErrors, setFormErrors] = useState({ email: "", phone: "" });
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekRangeLabel, setWeekRangeLabel] = useState("");

  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    if (currentView !== "home") {
      setCurrentView("home");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navigateToBooking = () => {
    setIsMenuOpen(false);
    setCurrentView("booking");
    window.scrollTo(0, 0);
  };

  const resetBooking = () => {
    setIsBookingSuccess(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setClientForm({ name: "", email: "", phone: "" });
    setFormErrors({ email: "", phone: "" });
    setIsGdprAccepted(false);
  };

  // EMAILJS SCRIPT BETÖLTÉSE (Biztonságosabb)
  useEffect(() => {
    if (document.getElementById("emailjs-script")) return; // Ne töltse be kétszer
    const script = document.createElement("script");
    script.id = "emailjs-script";
    script.src =
      "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
    script.async = true;
    script.onload = () => {
      if (window.emailjs) {
        window.emailjs.init(EMAILJS_CONFIG.publicKey);
      }
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      // Kiválasztás törlése, ha új hetet töltünk be
      setSelectedDate(null);
      setSelectedTime(null);

      setIsLoading(true);
      setApiError(null);
      setCalendarEvents([]);

      const today = new Date();
      const startOfWeek = new Date(today);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff + weekOffset * 7);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      // Az API-nak +7 nap kell (következő hétfő), hogy a vasárnap is benne legyen
      endOfWeek.setDate(endOfWeek.getDate() + 7);

      // JAVÍTÁS: A kiíráshoz viszont csak +6 napot (vasárnapot) használunk
      const displayEnd = new Date(startOfWeek);
      displayEnd.setDate(displayEnd.getDate() + 6);

      setWeekRangeLabel(
        `${formatDate(startOfWeek)} - ${formatDate(displayEnd)}`
      );

      const apiKey = GOOGLE_CALENDAR_CONFIG.apiKey;
      const calendarId = GOOGLE_CALENDAR_CONFIG.calendarId;

      if (!apiKey || apiKey.includes("IDE_MASOLD")) {
        setApiError("A naptár nincs konfigurálva (Hiányzó API kulcs).");
        setIsLoading(false);
        return;
      }

      try {
        // Az API hívásnál marad az endOfWeek (+7 nap), hogy a vasárnapi eseményeket is letöltse
        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
          calendarId
        )}/events?key=${apiKey}&timeMin=${startOfWeek.toISOString()}&timeMax=${endOfWeek.toISOString()}&singleEvents=true&orderBy=startTime`;
        const response = await fetch(url);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Google API Hiba: ${errorData.error.message}`);
        }

        const data = await response.json();

        if (data.items) {
          // Csak a "Szabad" vagy "Foglalható" nevű eseményeket szűrjük
          const freeEvents = data.items.filter(
            (event) =>
              event.summary &&
              (event.summary.toLowerCase().includes("szabad") ||
                event.summary.toLowerCase().includes("foglalható"))
          );

          const processedEvents = [];
          const now = new Date(); // Jelenlegi időpont a szűréshez

          freeEvents.forEach((event) => {
            const start = new Date(event.start.dateTime || event.start.date);

            // Múltbéli időpontok (napok és órák) kiszűrése
            if (start < now) {
              return;
            }

            const timeStr = start.toLocaleTimeString("hu-HU", {
              hour: "2-digit",
              minute: "2-digit",
            });
            const dateStr = formatDate(start);

            let dayGroup = processedEvents.find((p) => p.date === dateStr);
            if (!dayGroup) {
              const isOnline =
                event.location &&
                (event.location.toLowerCase().includes("online") ||
                  event.location.toLowerCase().includes("meet"));
              dayGroup = {
                fullDate: start,
                day:
                  getDayName(start).charAt(0).toUpperCase() +
                  getDayName(start).slice(1),
                date: dateStr,
                type: isOnline ? "online" : "personal",
                slots: [],
              };
              processedEvents.push(dayGroup);
            }
            // Kerüljük a duplikált időpontokat
            if (!dayGroup.slots.includes(timeStr)) {
              dayGroup.slots.push(timeStr);
            }
          });
          setCalendarEvents(processedEvents);
        }
      } catch (error) {
        console.error("Hiba:", error);
        setApiError(
          "Nem sikerült elérni a naptár adatait. Kérjük próbálja később."
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchEvents();
  }, [weekOffset]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let isValid = true;
    let errors = { email: "", phone: "" };
    if (!emailRegex.test(clientForm.email)) {
      errors.email = "Kérjük, adjon meg egy érvényes email címet.";
      isValid = false;
    }
    const phoneDigits = clientForm.phone.replace(/\D/g, "");
    if (phoneDigits.length < 9) {
      errors.phone = "A telefonszám túl rövid (min. 9 számjegy).";
      isValid = false;
    } else if (!phoneStructureRegex.test(clientForm.phone)) {
      errors.phone = "Érvénytelen karakterek.";
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };

  const handleBooking = async () => {
    if (!validateForm()) return;

    if (
      window.emailjs &&
      EMAILJS_CONFIG.serviceId !== "IDE_MASOLD_A_SERVICE_ID_T"
    ) {
      try {
        await window.emailjs.send(
          EMAILJS_CONFIG.serviceId,
          EMAILJS_CONFIG.templateId,
          {
            to_name: "Lévay Dóra",
            from_name: clientForm.name,
            from_email: clientForm.email,
            phone: clientForm.phone,
            date: selectedDate?.date,
            time: selectedTime,
            message: `Új foglalás: ${selectedDate?.date} - ${selectedTime}`,
          }
        );
      } catch (error) {
        console.error("Email küldési hiba:", error);
        alert(
          "Technikai hiba történt az email küldésekor, de rögzítettük az adatait."
        );
      }
    } else {
      console.warn("EmailJS nincs beállítva vagy Demo módban fut.");
    }
    setIsBookingSuccess(true);
  };

  const isFormValid =
    clientForm.name &&
    clientForm.email &&
    clientForm.phone &&
    selectedDate &&
    selectedTime &&
    isGdprAccepted;

  return (
    <div className="min-h-screen bg-[#FDFBF7] font-sans text-[#4A453E] selection:bg-[#E6E2DD] selection:text-[#4A453E]">
      <Navigation
        currentView={currentView}
        scrollToSection={scrollToSection}
        navigateToBooking={navigateToBooking}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
      {currentView === "home" && (
        <HomeView
          navigateToBooking={navigateToBooking}
          scrollToSection={scrollToSection}
        />
      )}
      {currentView === "booking" && (
        <BookingView
          scrollToSection={scrollToSection}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
          weekRangeLabel={weekRangeLabel}
          calendarEvents={calendarEvents}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          clientForm={clientForm}
          handleInputChange={handleInputChange}
          handleBooking={handleBooking}
          isFormValid={isFormValid}
          isGdprAccepted={isGdprAccepted}
          setIsGdprAccepted={setIsGdprAccepted}
          formErrors={formErrors}
          isBookingSuccess={isBookingSuccess}
          resetBooking={resetBooking}
          isLoading={isLoading}
          apiError={apiError}
        />
      )}
      <footer className="bg-[#4A453E] text-[#C8BFA9] py-16 border-t border-[#5D5750]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <img
                  src={LOGO_IMAGE_PATH}
                  alt="Logo"
                  className="h-10 w-auto opacity-80 mix-blend-screen grayscale"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <span className="text-3xl font-serif text-[#F0EBE5]">
                  NeuroNest
                </span>
              </div>
              <p className="text-[#8C857B] max-w-sm leading-relaxed">
                Pszichológiai támogatás neurodivergens gyermeket nevelő
                szülőknek.
              </p>
            </div>
            <div className="flex flex-col items-center md:items-end space-y-3">
              <a
                href="mailto:Levaydora.neuronest@gmail.com"
                className="hover:text-white transition-colors flex items-center text-lg"
              >
                <Mail className="h-5 w-5 mr-3" /> Levaydora.neuronest@gmail.com
              </a>
              <p className="text-sm pt-4 border-t border-[#5D5750] w-full text-center md:text-right text-[#756F66]">
                © {new Date().getFullYear()} Lévay Dóra. Minden jog fenntartva.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
